import React, {ReactNode, useEffect} from "react";
import {makeStyles} from "@mui/styles";
import cx from "classnames";
import CloseIcon from "@mui/icons-material/Close";
import {AppButton} from "./AppButton";
import {useI18n} from "../../i18n/I18nContext";
import {Box} from "@mui/material";

const useStyles = makeStyles(() => ({
    root: {
        width: "100vw",
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        background: "rgba( 255, 255, 255, 0.25 )",
        boxShadow: "0 8px 32px 0 rgba( 31, 38, 135, 0.37 )",
        backdropFilter: "blur( 6px )",
        zIndex: 1000,
        transform: "translateY(-130%)",
        transition: "0.3s",
        "&.show": {
            transform: "translateY(0)",
        },
    },
    content: {
        minWidth: "545px",
        padding: "36px 45px",
        borderRadius: "20px",
        background: "#fff",
        position: "absolute",
        top: "50%",
        left: "50%",
        boxShadow:
            "rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px",
        transform: "translate(-50%,-50%)",
    },
    overflow: {
        width: "100%",
        height: "100%",
    },
    close: {
        position: "absolute",
        top: "10px",
        right: "10px",
        cursor: "pointer",
        width: "24px",
        height: "24px",
        background: "#E5E5E5",
        borderRadius: "50%",
    },
    contentBody: {
        maxHeight: "70vh",
        overflow: "auto",
        textAlign: "center"
    },
    questionTitle: {
        marginBottom: 10
    },
    questionBody: {
        marginBottom: 10
    },
    warningRoot: {
        padding: 15,
        background: "#FDE9D9",
        borderLeft: "3px solid #E12D3A",
        borderRadius: 5,
        margin: "15px 0",
        textAlign: "left",
        color: "#C52707",
        "& h4": {
            marginBottom: 10
        }
    }
}));

interface Props {
    readonly show: boolean;
    readonly onClose: () => void;
    readonly children: ReactNode;
    readonly onAgree: () => void;
    readonly onCancel?: () => void;
    readonly questionTitle?: string;
    readonly questionBody?: string;
    readonly warningMessage?: string;
}

export function DialogWindow({
                                 show,
                                 onClose,
                                 onAgree,
                                 children,
                                 onCancel,
                                 warningMessage,
                                 questionTitle = "ARE_YOU_SURE",
                                 questionBody = "REALLY_DELETE",
                             }: Props) {
    useEffect(() => {
        window.addEventListener("keyup", (e) => {
            if (e.keyCode === 27 && show) onClose();
        });
    }, [show, onClose]);

    const classes = useStyles();
    const {translate} = useI18n();

    return (
        <div className={cx(classes.root, {show})}>
            <div className={classes.overflow} onClick={() => onClose()}/>
            <div className={classes.content}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                }} className={classes.close}>
                    <CloseIcon sx={{fontSize: 15}} onClick={() => onClose()}/>
                </Box>
                <div className={classes.contentBody}>
                    <h2 className={classes.questionTitle}>{translate(questionTitle)}</h2>
                    <p className={classes.questionBody}>{translate(questionBody)}</p>
                    {warningMessage && (
                        <div className={classes.warningRoot}>
                            <h4>{translate("WARNING")}</h4>
                            <p>{translate(warningMessage)}</p>
                        </div>
                    )}
                    <div>
                        <AppButton sx={{mx: 1}} onClick={onAgree}>{translate("I_AGREE")}</AppButton>
                        <AppButton
                            sx={{mx: 1}}
                            background="#d1d1d1"
                            onClick={() => {
                                onClose();
                                onCancel && onCancel();
                            }}
                        >
                            {translate("CANCEL")}
                        </AppButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
